/* eslint-disable react-hooks/exhaustive-deps */
import { has } from '@/commonFunctions/utils'
import { Button, ButtonPrimary } from '@/components/button'
import { LoadingScreen } from '@/components/common'
import { ArrowBack } from '@/components/icons'
import {
  AutocompleteAddress,
  AutocompleteLocationInput,
  DatePickerInput,
  LogoInput,
  TextArea,
  TextInput,
} from '@/components/input'
import { MainLayout } from '@/components/layouts'
import { ConfirmModal, WarningLeagueModal } from '@/components/modal'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { CLUB_MODIFY } from '@/constants/Common'
import { EVENT_ANNOUNCEMENT_TITLE_MAX, EVENT_NAME_REQUIRED } from '@/constants/MessageConstants'
import { ROUTE_CLUBS } from '@/constants/Routes'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import {
  saveAddress as saveAddressApi,
  uploadImage,
  useDraftEvent,
  useGetEvent,
  useSaveAddress,
  useSaveEvent,
} from '@/services'
import { PayloadSaveEvent } from '@/types'
import CreateEventValidationSchema from '@/utils/schema/CreateEventValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { isEmpty } from 'lodash'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { getClubRoles } from '../clubSlice'
import { clubSelector } from '../selectors'

export type CreateNewEventForm = {
  logo: File | null
  logoEdit?: { url: string; id: number }
  leagueName: string
  brackets: [
    {
      bracketId: 5764514422
      durationDateTime: ['2024-08-01T01:00:00', '2024-08-01T02:00:00']
      format: 'SINGLES'
      playerGroup: 'MEN'
      registrationDateTime: ['2024-08-01T01:00:00', '2024-08-01T02:00:00']
    },
  ]
  registrationUrl?: string
  address: AutocompleteAddress
  longDescription: string
  longDescriptionContentId?: number
  startDate: string
  endDate: string
}

export const CreateNewEvent = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const params = useParams()
  const dispatch = useAppDispatch()
  const { isShowMenu, isShowDropdown } = useAppSelector(appSelector)
  const { permissions } = useAppSelector(clubSelector)
  const { user } = useAppSelector(userSelector)

  const { clubId, eventId } = params

  const [flexLeagueWarning, setFlexLeagueWarning] = useState(false)
  const [openDraftModal, setOpenDraftModal] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const defaultValues: CreateNewEventForm = {
    logo: null,
    leagueName: '',
    registrationUrl: '',
    brackets: [
      {
        bracketId: 5764514422,
        durationDateTime: ['2024-08-01T01:00:00', '2024-08-01T02:00:00'],
        format: 'SINGLES',
        playerGroup: 'MEN',
        registrationDateTime: ['2024-08-01T01:00:00', '2024-08-01T02:00:00'],
      },
    ],
    address: {
      description: '',
      placeId: '',
    },
    longDescription: '',
    startDate: '',
    endDate: '',
  }

  const { mutate: saveEvent } = useSaveEvent()
  const { mutate: draftEvent } = useDraftEvent()
  const { data: eventData } = useGetEvent(eventId)
  const { mutate: saveAddress } = useSaveAddress()

  const {
    setValue,
    control,
    handleSubmit,
    register,
    watch,
    setError,
    getValues,
    formState: { errors, isDirty },
  } = useForm<CreateNewEventForm>({
    defaultValues,
    resolver: yupResolver(CreateEventValidationSchema as any),
    shouldFocusError: false,
    mode: 'onTouched',
  })

  const onSubmit = handleSubmit(async (data) => {
    setIsLoading(true)
    let responseImg = null

    if (clubId && user) {
      if (data.logo) {
        responseImg = await uploadImage(data.logo)
      }
      const payload: PayloadSaveEvent = {
        clubId: clubId,
        leagueName: data.leagueName,
        registrationUrl: data.registrationUrl,
        brackets: data.brackets,
        startDate: data.startDate,
        endDate: data.endDate,
        longDescription: { content: data.longDescription, contentType: 'text/html' },
        userId: user.id,
        ...(responseImg && { mediaId: responseImg.result.id }),
      }
      saveAddress(
        { placeId: data.address.placeId },
        {
          onSuccess: (data) => {
            const reqData = {
              ...payload,
              addressId: data.id,
            }
            saveEvent(reqData, {
              onSuccess: () => {
                navigate(`${ROUTE_CLUBS}/${clubId}`)
                setIsLoading(false)
              },
              onError: (errors: any) => {
                toast.error(errors.response.data.message)
                setIsLoading(false)
              },
            })
          },
        }
      )
    }
  })

  const onDraftEvent = async () => {
    if (!watch('leagueName')) {
      onCloseDraftModal()
      return setError('leagueName', { message: EVENT_NAME_REQUIRED, type: 'required' })
    }
    if (watch('leagueName').length > 150 && isDirty) {
      onCloseDraftModal()
      return setError('leagueName', { message: EVENT_ANNOUNCEMENT_TITLE_MAX, type: 'maxLength' })
    }

    const currentFormValues = getValues()
    setIsLoading(true)
    let responseImg = null

    if (clubId && user) {
      if (currentFormValues.logo) {
        responseImg = await uploadImage(currentFormValues.logo)
      }

      let placeId
      if (!eventId && currentFormValues.address.placeId) {
        const result = await saveAddressApi({ placeId: currentFormValues.address.placeId })
        placeId = result.result.id
      }
      if (eventId) placeId = currentFormValues.address.placeId
      const payload: PayloadSaveEvent = {
        ...(eventId && { leagueId: Number(eventId) }),
        ...(placeId && { addressId: Number(placeId) }),
        clubId: clubId,
        leagueName: currentFormValues.leagueName,
        registrationUrl: currentFormValues.registrationUrl,
        ...(currentFormValues.logoEdit && { mediaId: currentFormValues.logoEdit.id }),
        longDescription: {
          content: currentFormValues.longDescription,
          contentType: 'text/html',
          ...(currentFormValues.longDescriptionContentId && { contentId: currentFormValues.longDescriptionContentId }),
        },
        userId: user.id,
        ...(responseImg && { mediaId: responseImg.result.id }),
      }
      draftEvent(payload, {
        onSuccess: () => {
          navigate(`${ROUTE_CLUBS}/${clubId}`)
          onCloseDraftModal()
          setIsLoading(false)
        },
        onError: (errors: any) => {
          toast.error(errors.response.data.message)
          setIsLoading(false)
        },
      })
    }
  }

  const onDiscardEvent = () => {
    onCloseDraftModal()
    navigate(`${ROUTE_CLUBS}/${clubId}`)
  }

  const onOpenDraftModal = () => setOpenDraftModal(true)

  const onCloseDraftModal = () => setOpenDraftModal(false)

  const onGoBack = () => {
    if (eventId) onOpenDraftModal()
    if (!eventId && !isDirty) navigate(`${ROUTE_CLUBS}/${clubId}`)
    if (!eventId && isDirty) onOpenDraftModal()
  }

  useEffect(() => {
    if (clubId && user?.id) {
      dispatch(getClubRoles({ clubId: Number(clubId), userId: user?.id }))
    }
  }, [clubId, user])

  useEffect(() => {
    if (permissions && !has(permissions, CLUB_MODIFY))
      navigate(`${ROUTE_CLUBS}/${clubId}`, { relative: 'path', replace: true })
  }, [permissions])

  useEffect(() => {
    if (eventData) {
      setValue('logoEdit', { url: eventData.result.mediaUrl, id: eventData.result.mediaId })
      setValue('leagueName', eventData.result?.leagueName, {
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('address', {
        description: eventData.result.address?.formattedAddress
          ? eventData.result.address?.formattedAddress
          : eventData.result.address?.shortAddress || '',
        placeId: eventData.result.address?.id.toString() || '',
      })
      setValue('longDescription', eventData.result?.longDescription?.content)
      setValue('longDescriptionContentId', eventData.result?.longDescription?.contentId)
    }
  }, [eventData])

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}
      <MainLayout>
        <MainSidebar />
        <div className={`transition-all duration-500 ease-in-out ${isShowMenu && 'lg:ml-56 2xl:ml-72'}`}>
          <div className="flex flex-col gap-y-4 max-w-6xl mx-auto mb-20 lg:mb-8">
            {/* Navigator */}
            <div className="flex items-center justify-between">
              <div className="flex gap-x-3 items-center">
                <Button onClick={onGoBack} className="hover:opacity-50">
                  <ArrowBack className="text-white" />
                </Button>
                <h1 className="text-sm md:text-xl text-white font-semibold">{t('club.createNewEvent')}</h1>
              </div>
              <ButtonPrimary
                title="Save"
                className="py-3 px-5"
                disabled={isLoading}
                onClick={() => {
                  if (isEmpty(errors)) {
                    onSubmit()
                  } else {
                    if (typeof errors === 'object' && Object.values(errors)[0]) {
                      toast.error(Object.values(errors)[0]?.message || 'There are some validation errors. Please check')
                    } else {
                      toast.error('There are some validation errors. Please check')
                    }
                  }
                }}
              />
            </div>

            {/* Main content */}
            <div className="bg-white rounded-xl p-4">
              {/* Event details */}
              <div className="md:col-span-3 flex flex-col gap-y-4">
                <LogoInput
                  onSave={(logo: File | null) => setValue('logo', logo, { shouldValidate: true })}
                  label={t('common.logo')}
                  defaultSrc={eventId ? watch('logoEdit.url') : watch('logo')}
                  error={errors.logo?.message}
                  onRemoveProfileLogo={() =>
                    setValue('logo', null, {
                      shouldValidate: true,
                    })
                  }
                  uploadImageModalTitle={t('common.eventLogo')}
                  labelClassName={'!text-navy-900'}
                />
                <TextInput
                  label={t('common.eventName')}
                  labelClassName="!text-navy-900"
                  placeholder={t('common.eventName')}
                  {...register('leagueName')}
                  error={errors?.leagueName?.message}
                />

                <TextInput
                  label={t('common.registration_url')}
                  labelClassName="!text-navy-900"
                  placeholder={t('common.registration_url')}
                  {...register('registrationUrl')}
                  error={errors?.registrationUrl?.message}
                />

                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <AutocompleteLocationInput
                      {...field}
                      label={t('common.location')}
                      labelClassName="!text-navy-900"
                      setValue={(location) =>
                        location &&
                        setValue('address', location, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                      error={errors?.address?.description?.message}
                    />
                  )}
                />

                <div className="flex w-full">
                  <DatePickerInput
                    label={t('common.startDateTime')}
                    labelClassName="!text-navy-900"
                    containerClassName="w-full pr-2"
                    selected={watch('startDate')}
                    showTimeInput
                    timeInputLabel="Time:"
                    dateFormat="MMM dd, yyyy h:mm aa"
                    onChange={(date: Date | null) => {
                      date &&
                        setValue('startDate', moment(date).format('YYYY-MM-DDTHH:mm:ss'), {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                    }}
                    error={errors?.startDate?.message}
                  />

                  <DatePickerInput
                    label={t('common.endDateTime')}
                    labelClassName="!text-navy-900"
                    containerClassName="w-full pl-2"
                    selected={watch('endDate')}
                    showTimeInput
                    timeInputLabel="Time:"
                    dateFormat="MMM dd, yyyy h:mm aa"
                    onChange={(date: Date | null) => {
                      date &&
                        setValue('endDate', moment(date).format('YYYY-MM-DDTHH:mm:ss'), {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                    }}
                    error={errors?.endDate?.message}
                  />
                </div>
                <Controller
                  name="longDescription"
                  control={control}
                  render={({ field, formState }) => (
                    <TextArea
                      hasBorder={true}
                      label={t('common.summary')}
                      labelClassName="!text-navy-900"
                      placeholder={t('common.whatAreTheDetails')}
                      error={errors?.longDescription?.message}
                      defaultValue={formState.defaultValues?.longDescription}
                      {...field}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
      {isLoading && <LoadingScreen />}
      <WarningLeagueModal open={flexLeagueWarning} onClose={() => setFlexLeagueWarning(false)} />
      <ConfirmModal
        open={openDraftModal}
        onClose={onCloseDraftModal}
        onCloseButton={onDiscardEvent}
        onSubmit={onDraftEvent}
        title="Exit Event Creation?"
        desc="If you'd like to exit event creation process you may save your event as a draft."
        cancelTitle="Discard"
        submitTitle="Save As Draft"
      />
    </>
  )
}
